import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { withSentryReactRouterV6Routing } from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider } from "@/hooks/use-auth";
import { DomainGlossaryProvider } from "@/hooks/use-domain-glossary-tips";

// Auth components
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PublicRoute } from "@/components/auth/PublicRoute";
import { LegacyAuthRedirect } from "@/components/auth/LegacyAuthRedirect";
import {
  LEGACY_AUTH_PATH,
  SIGN_IN_PATH,
  SIGN_UP_PATH,
} from "@/lib/auth-routes";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { FeatureFlagRoute } from "@/components/auth/FeatureFlagRoute";
import { PermissionRoute } from "@/components/auth/PermissionRoute";

// Layouts
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";

// Public Pages
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AcceptInvite from "@/pages/AcceptInvite";
import NotFound from "@/pages/NotFound";

// App Pages
import Dashboard from "@/pages/Dashboard";
import Deals from "@/pages/Deals";
import Pipeline from "@/pages/Pipeline";
import Properties from "@/pages/Properties";
import Rehab from "@/pages/Rehab";
import Financing from "@/pages/Financing";
import Rentals from "@/pages/Rentals";
import PropertyManagement from "@/pages/PropertyManagement";
import Section8 from "@/pages/Section8";
import Accounting from "@/pages/Accounting";
import Taxes from "@/pages/Taxes";
import Settings from "@/pages/Settings";

// Admin Pages
import AdminWorkspaces from "@/pages/admin/Workspaces";
import AdminUsers from "@/pages/admin/Users";
import AdminFeatureFlags from "@/pages/admin/FeatureFlags";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminSupport from "@/pages/admin/Support";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch when the user tabs away and comes back. The default
      // (`true`) caused in-progress UI work (e.g. calculation drafts on the
      // Find page, the open Manage Columns dialog on the Pipeline page) to
      // appear "reset" because every active query refetched on focus.
      refetchOnWindowFocus: false,
      // Treat data as fresh for 60s — avoids an immediate refetch when a
      // component re-mounts, which made navigation between pages feel like
      // a reload. Mutations still invalidate explicitly, so writes stay
      // reflected instantly.
      staleTime: 60 * 1000,
    },
  },
});

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark">
      <AuthProvider>
        <DomainGlossaryProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SentryRoutes>
              {/* Public Routes - Landing page accessible to all */}
              <Route path="/" element={<Landing />} />

              {/* Auth Routes - Redirect to dashboard if already logged in.
                  Sign in and sign up are separate URLs so each can be shared
                  and linked on its own; `/auth` forwards to them. */}
              <Route element={<PublicRoute />}>
                <Route path={SIGN_IN_PATH} element={<Auth />} />
                <Route path={SIGN_UP_PATH} element={<Auth />} />
                <Route path={LEGACY_AUTH_PATH} element={<LegacyAuthRedirect />} />
              </Route>

              {/* Invite acceptance — outside ProtectedRoute; user has a partial session from the invite link */}
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Protected App Routes (Investor) */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  <Route
                    path="/dashboard"
                    element={
                      <FeatureFlagRoute flagKey="dashboard_module">
                        <Dashboard />
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/deals"
                    element={
                      <FeatureFlagRoute flagKey="deals_module">
                        {/* PermissionRoute permission="deals.view" disabled — deals permission removed */}
                        <Deals />
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/pipeline"
                    element={
                      <FeatureFlagRoute flagKey="pipeline_module">
                        <PermissionRoute permission="pipeline.view">
                          <Pipeline />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/properties"
                    element={
                      <FeatureFlagRoute flagKey="properties_module">
                        <PermissionRoute permission="properties.view">
                          <Properties />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/rehab"
                    element={
                      <FeatureFlagRoute flagKey="rehab_module">
                        <PermissionRoute permission="rehab.view">
                          <Rehab />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/financing"
                    element={
                      <FeatureFlagRoute flagKey="financing_module">
                        <PermissionRoute permission="loans.view">
                          <Financing />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/rentals"
                    element={
                      <FeatureFlagRoute flagKey="rentals_module">
                        <PermissionRoute permission="rentals.view">
                          <Rentals />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/property-mgmt"
                    element={
                      <FeatureFlagRoute flagKey="property_mgmt_module">
                        <PermissionRoute permission="property_mgmt.view">
                          <PropertyManagement />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/section-8"
                    element={
                      <FeatureFlagRoute flagKey="section8_module">
                        <PermissionRoute permission="section8.view">
                          <Section8 />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/accounting"
                    element={
                      <FeatureFlagRoute flagKey="accounting_module">
                        <PermissionRoute permission="accounting.view">
                          <Accounting />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/taxes"
                    element={
                      <FeatureFlagRoute flagKey="taxes_module">
                        <PermissionRoute permission="accounting.view">
                          <Taxes />
                        </PermissionRoute>
                      </FeatureFlagRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <FeatureFlagRoute flagKey="settings_module">
                        <Settings />
                      </FeatureFlagRoute>
                    }
                  />
                </Route>

                {/* Protected Admin Routes — super_admin only */}
                <Route element={<AdminRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<Navigate to="/admin/workspaces" replace />} />
                    <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
                    <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
                    <Route path="/admin/support" element={<AdminSupport />} />
                  </Route>
                </Route>
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
          </BrowserRouter>
        </TooltipProvider>
        </DomainGlossaryProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
